import { Injectable, NgZone, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { environment } from '../../environments/environment';

const NOT_ALLOWED_URL = '/not-allowed';

/**
 * Detects whether the browser DevTools are open (docked or undocked) and
 * exposes a `blocked` signal so guards/components can react.
 *
 * Two independent heuristics are combined:
 * - Window size gap between outer/inner dimensions (catches docked DevTools).
 * - A console object-preview trap (catches undocked/detached DevTools, since
 *   preview generation only runs when a DevTools frontend is attached).
 *
 * Detection only runs when `environment.enableDevToolsBlocker` is enabled.
 */
@Injectable({ providedIn: 'root' })
export class DevToolsDetectionService {
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly ngZone = inject(NgZone);

  private static readonly SIZE_THRESHOLD = 160;
  private static readonly POLL_INTERVAL_MS = 700;
  private static readonly CONSOLE_CHECK_EVERY_N_POLLS = 3;
  private static readonly DEBOUNCE_POLLS = 2;

  private readonly _blocked = signal(false);
  readonly blocked = this._blocked.asReadonly();

  private pollHandle: ReturnType<typeof setInterval> | null = null;
  private pollCount = 0;
  private consecutiveOpen = 0;
  private consecutiveClosed = 0;
  private previousUrl: string | null = null;
  private lastConsoleOpen = false;

  constructor() {
    if (environment.enableDevToolsBlocker) {
      this.start();
    }
  }

  start(): void {
    if (this.pollHandle) {
      return;
    }
    this.ngZone.runOutsideAngular(() => {
      this.pollHandle = setInterval(() => this.poll(), DevToolsDetectionService.POLL_INTERVAL_MS);
    });
  }

  stop(): void {
    if (this.pollHandle) {
      clearInterval(this.pollHandle);
      this.pollHandle = null;
    }
  }

  protected exceedsThreshold(outer: number, inner: number): boolean {
    return outer - inner > DevToolsDetectionService.SIZE_THRESHOLD;
  }

  protected isDockedOpen(): boolean {
    return (
      this.exceedsThreshold(window.outerWidth, window.innerWidth) ||
      this.exceedsThreshold(window.outerHeight, window.innerHeight)
    );
  }

  protected createConsoleTrap(): { trap: object; wasAccessed: () => boolean } {
    let accessed = false;
    const trap = {};
    Object.defineProperty(trap, 'id', {
      get: () => {
        accessed = true;
        return '';
      },
      configurable: true,
      enumerable: true,
    });
    return { trap, wasAccessed: () => accessed };
  }

  protected isConsoleOpen(): boolean {
    const { trap, wasAccessed } = this.createConsoleTrap();
    // eslint-disable-next-line no-console
    console.log(trap);
    return wasAccessed();
  }

  private poll(): void {
    this.pollCount++;
    const dockedOpen = this.isDockedOpen();
    const checkConsole = this.pollCount % DevToolsDetectionService.CONSOLE_CHECK_EVERY_N_POLLS === 0;
    if (checkConsole) {
      this.lastConsoleOpen = this.isConsoleOpen();
    }
    const detectedNow = dockedOpen || this.lastConsoleOpen;

    if (detectedNow) {
      this.consecutiveOpen++;
      this.consecutiveClosed = 0;
    } else {
      this.consecutiveClosed++;
      this.consecutiveOpen = 0;
    }

    if (!this._blocked() && this.consecutiveOpen >= DevToolsDetectionService.DEBOUNCE_POLLS) {
      this.ngZone.run(() => this.activateBlock());
    } else if (this._blocked() && this.consecutiveClosed >= DevToolsDetectionService.DEBOUNCE_POLLS) {
      this.ngZone.run(() => this.deactivateBlock());
    }
  }

  private activateBlock(): void {
    const currentUrl = this.router.url;
    if (currentUrl !== NOT_ALLOWED_URL) {
      this.previousUrl = currentUrl;
    }
    this._blocked.set(true);
    this.dialog.closeAll();
    this.router.navigateByUrl(NOT_ALLOWED_URL).then(() => this.dialog.closeAll());
  }

  private deactivateBlock(): void {
    this._blocked.set(false);
    const target = this.previousUrl && this.previousUrl !== NOT_ALLOWED_URL ? this.previousUrl : '/';
    this.previousUrl = null;
    this.router.navigateByUrl(target);
  }
}
